import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faFile, faFileImage, faFilePdf } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { TicketAttachmentMeta } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { formatFileSize } from "@/components/SupportTicket/ticketAttachments";
import { classNames } from "@/utils/classNames";

import { useTicketAttachmentSource } from "../hooks/ticketAttachmentSource";
import { useTicketAttachmentUrl } from "../hooks/useTicketAttachmentUrl";
import { deliverAttachmentBlob, isInlineAttachment } from "../utils/ticketAttachmentOpen";
import { isImageAttachment } from "../utils/ticketThread";

interface TicketAttachmentChipProps {
	attachment: TicketAttachmentMeta;
	/** Render an image as an inline thumbnail (thread bubbles); a non-image ignores it. */
	thumbnail?: boolean;
	/** Called with the file name when the authenticated fetch fails; the host shows a snackbar. */
	onError: (fileName: string) => void;
}

const iconFor = (mimeType: string): IconDefinition => {
	if (mimeType.startsWith("image/")) {
		return faFileImage;
	}

	return mimeType === "application/pdf" ? faFilePdf : faFile;
};

/**
 * One attachment as the thread and the rail show it (PREC-3938 decision 1). The download route
 * needs the JWT, so a click fetches through RTK Query — through whichever blob hooks the page
 * provides via `TicketAttachmentSourceContext` (customer by default, staff on `/app/tickets/:id`,
 * PREC-3940); for an inline type the tab is opened **before** the await, so the browser still
 * counts it as the click's own popup.
 */
export const TicketAttachmentChip: FunctionComponent<TicketAttachmentChipProps> = ({
	attachment,
	thumbnail = false,
	onError,
}) => {
	const { t } = useTranslation();
	const { useLazyBlobQuery } = useTicketAttachmentSource();
	const [fetchBlob] = useLazyBlobQuery();
	const showThumbnail = thumbnail && isImageAttachment(attachment);
	const thumbnailUrl = useTicketAttachmentUrl(attachment, showThumbnail);

	const open = async () => {
		const tab = isInlineAttachment(attachment.mimeType) ? window.open("", "_blank") : null;

		try {
			const blob = await fetchBlob({
				ticketId: attachment.ticketId,
				attachmentId: attachment.id,
			}).unwrap();

			deliverAttachmentBlob(blob, attachment, tab);
		} catch {
			tab?.close();
			onError(attachment.fileName);
		}
	};

	return (
		<button
			aria-label={t("support.detail.attachments.open", { name: attachment.fileName })}
			className={classNames(
				"cursor-pointer rounded-lg border border-gray-200 text-left transition-colors duration-200 hover:border-brand-light",
				showThumbnail
					? "flex w-40 flex-col gap-1.5 p-1.5"
					: "inline-flex h-8 max-w-full items-center px-2.5"
			)}
			type="button"
			onClick={() => void open()}
		>
			{showThumbnail && (
				<span className="flex h-24 w-full items-center justify-center overflow-hidden rounded-md bg-status-info-light">
					{thumbnailUrl ? (
						<img
							alt={attachment.fileName}
							className="h-full w-full object-cover"
							src={thumbnailUrl}
						/>
					) : (
						<Icon className="text-text-meta" icon={faFileImage} />
					)}
				</span>
			)}
			<span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-text">
				<Icon className="shrink-0 text-text-meta" icon={iconFor(attachment.mimeType)} />
				<span className="truncate">{attachment.fileName}</span>
				<span className="shrink-0 text-text-meta">{formatFileSize(attachment.sizeBytes)}</span>
			</span>
		</button>
	);
};
